import { useNavigate } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useLandingTarget } from "../hooks/useDefaultRoute";

interface AppCard {
  id: string;
  name: string;
  description: string;
  logo: string;       // single letter / emoji for the icon
  color: string;      // tailwind bg color for the icon circle
  href?: string;      // internal route (uses navigate)
  externalUrl?: string; // external URL (opens in new tab)
  status: "live" | "coming-soon";
}

const COMPANY_APPS: AppCard[] = [
  {
    id: "employee-hub",
    name: "MGC Hub",
    description: "Payslips, loans, leave, tasks, and more — your HR self-service portal.",
    logo: "B",
    color: "bg-[#2447d9]",
    href: "/employee/dashboard",
    status: "live",
  },
  {
    id: "isp-portal",
    name: "ISP Portal",
    description: "Internet service management and subscriber portal.",
    logo: "I",
    color: "bg-[#0891b2]",
    externalUrl: "http://62.146.234.101:3000",
    status: "live",
  },
  {
    id: "isp-billing",
    name: "ISP Billing",
    description: "Billing management and payment tracking for internet services.",
    logo: "B",
    color: "bg-[#7c3aed]",
    externalUrl: "https://bghitsolutions.com/login.php",
    status: "live",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading, setActiveBusiness } = useAuth();
  const resolveTarget = useLandingTarget();

  const handleAppClick = (app: AppCard) => {
    if (loading) return;
    if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (app.href) {
      if (!user) {
        navigate("/login");
        return;
      }
      if (app.id === "employee-hub") {
        // Land on a business + page the user can actually reach (avoids /unauthorized loops).
        const { businessId, route } = resolveTarget();
        if (businessId) setActiveBusiness(businessId);
        navigate(route);
      } else {
        navigate(app.href);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f1f5c] via-[#1a2f7a] to-[#0a1640] flex flex-col">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/mgc_logo.jpeg" alt="MGC Logo" className="h-9 object-contain" />
          <div>
            <span className="text-base font-extrabold text-white leading-none tracking-tight">MDM Group of Company</span>
          </div>
        </div>
        {user && (
          <button
            onClick={() => {
              const { businessId, route } = resolveTarget();
              if (businessId) setActiveBusiness(businessId);
              navigate(route);
            }}
            className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-xs font-medium text-white hover:bg-white/20 transition-colors"
          >
            Dashboard
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </header>

      {/* Main */}
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-16">
        <div className="w-full max-w-3xl">
          {/* Title */}
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-300/70 mb-2">
              Company Portal
            </p>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
              Your workplace apps
            </h1>
            <p className="mt-2 text-sm text-white/50">
              All your company tools in one place.
            </p>
          </div>

          {/* App grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COMPANY_APPS.map((app) => (
              <button
                key={app.id}
                onClick={() => handleAppClick(app)}
                disabled={loading || app.status === "coming-soon"}
                className={[
                  "group relative flex flex-col gap-4 rounded-2xl border border-white/15 bg-white/8 backdrop-blur-sm p-5 text-left",
                  "hover:bg-white/15 hover:border-white/30 hover:shadow-[0_8px_32px_0_rgb(0_0_0/0.24)]",
                  "transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/40",
                  app.status === "coming-soon" && "opacity-50 cursor-not-allowed",
                ].join(" ")}
              >
                {/* App icon */}
                <div className="flex items-start justify-between">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${app.color} shadow-lg`}>
                    <span className="text-xl font-extrabold text-white">{app.logo}</span>
                  </div>
                  {app.status === "coming-soon" ? (
                    <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/60">
                      Coming soon
                    </span>
                  ) : app.externalUrl ? (
                    <ExternalLink className="h-4 w-4 text-white/40 group-hover:text-white/70 transition-colors" />
                  ) : (
                    <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-white/70 transition-transform group-hover:translate-x-0.5" />
                  )}
                </div>

                {/* App info */}
                <div>
                  <p className="font-semibold text-white leading-tight">{app.name}</p>
                  <p className="mt-1 text-xs text-white/55 leading-snug">{app.description}</p>
                </div>

                {/* Open label */}
                {app.status === "live" && (
                  <div className="mt-auto">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80 group-hover:bg-white/20 transition-colors">
                      {loading ? "Loading…" : !user && !app.externalUrl ? "Sign in to open" : "Open"}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-[11px] text-white/30">
        © {new Date().getFullYear()} MGC · Internal use only
      </footer>
    </div>
  );
}
